const DummyPrintDriver = require('./Drivers/DummyPrintDriver');
const EventNames = require('@/lib/Events/EventNames');
const logFactory = require('@/libraries/DigiTickets/Logging/logFactory');
const printCssString = require('@temp/js/printCss');
const PrintDriverRefs = require('@/lib/Printing/PrintDriverRefs');
const PrinterAppPrintDriver = require('./Drivers/PrinterAppPrintDriver');
const PrintJobOptions = require('./PrintJobOptions');
const PrintPrintDriver = require('@/lib/Printing/Drivers/PrintPrintDriver');
const SKIP_PRINTING = require('@/lib/Printing/SkipPrinting');
const { generateHeadings } = require('@/lib/Printing/printerHelperFunctions');

class Printer {
    /**
     * Gives us the facility to print from our application without worrying about the logistics of doing so.
     *
     * @param $injector
     * @param $templateCache This is different from the angular-cache template cache.
     *                       This service provides the Angular templates compiled during the build process.
     * @param $timeout
     * @param {EventBus} eventBus
     * @param {PrintRouter} printRouter
     * @param {TemplateCompiler} templateCompiler
     */
    constructor(
        $injector,
        $templateCache,
        $timeout,
        eventBus,
        printRouter,
        templateCompiler
    ) {
        this.$injector = $injector;
        this.$templateCache = $templateCache;
        this.$timeout = $timeout;
        this.eventBus = eventBus;
        this.printRouter = printRouter;
        this.templateCompiler = templateCompiler;

        this.logger = logFactory('Printer');

        /**
         * Has a document been sent for printing but its promise hasn't resolved yet?
         *
         * @type {boolean}
         */
        this.isPrinting = false;

        /**
         * Reference to the #print-iframe in the document.
         *
         * @type {Window}
         */
        this.printFrame = typeof window !== 'undefined' ? window.frames['print-iframe'] : null;

        /**
         * Items that are queued up to print.
         *
         * @type {{content: string, opts: PrintJobOptions}[]}
         */
        this.printQueue = [];
    }

    printNext() {
        const queuedItem = this.printQueue.shift();
        if (queuedItem) {
            this.isPrinting = true;
            this.printContent(
                queuedItem.content,
                queuedItem.opts
            )
                .then(() => this.printNext())
                .catch(() => this.printNext());
        } else {
            this.isPrinting = false;
        }
    }

    /**
     * @param {string} content
     * @param {PrintJobOptions} opts
     */
    addToPrintQueue(content, opts) {
        this.printQueue.push({ content, opts });
        if (!this.isPrinting) {
            this.printNext();
        }
    }

    /**
     * @param {string} content
     * @param {PrintJobOptions} opts
     *
     * @return {Promise}
     */
    printContent(content, opts) {
        if (!opts) {
            opts = new PrintJobOptions('');
        }

        if (!opts.printRouting) {
            opts.printRouting = this.printRouter.getRoutingForType(opts.printType);
        }
        const driverRef = opts.printRouting.driverRef;

        if (opts?.printRouting?.printerName === SKIP_PRINTING) {
            console.log('Printing skipped', opts.printType, driverRef, opts.printRouting.printerName);
            this.eventBus.broadcast(EventNames.PRINTING_FINISHED, { driverRef });
            return Promise.resolve();
        }

        // Determine which print driver to use.
        // This happens here instead of in the constructor because the current device
        // may not be available yet in the constructor.
        let printDriverClass;

        switch (driverRef) {
            case PrintDriverRefs.DUMMY:
                printDriverClass = DummyPrintDriver;
                break;

            case PrintDriverRefs.PRINTER_APP:
                printDriverClass = PrinterAppPrintDriver;
                break;

            case PrintDriverRefs.PRINT:
            default:
                printDriverClass = PrintPrintDriver;
                break;
        }

        let printDriver = this.$injector.instantiate(printDriverClass);

        // printCssString is built by Grunt and contains the contents of the built print.css file
        // which comes from src/print/less/print.less
        this.printFrame.document.documentElement.innerHTML = content.replace('<!-- printCss -->', `<style type="text/css">${printCssString}</style>`);

        console.log('Printing', opts.printType, driverRef, opts.printRouting.printerName);

        return printDriver
            .print(
                this.printFrame,
                opts
            )
            .then(() => {
                // Printed completed successfully.
                // This function should always be defined. It is no longer a user-defined callback
                // but is now an internal callback which will trigger resolving the printing promise.
                opts.onSuccess();
            })
            .catch((e) => {
                // Printing failed.
                this.logger.error(
                    'Printing failed',
                    e,
                    {
                        driverRef,
                    }
                );
                // This function should always be defined. It is no longer a user-defined callback
                // but is now an internal callback which will trigger rejecting the printing promise.
                opts.onFailure(e);

                throw e;
            })
            .finally(() => {
                this.eventBus.broadcast(EventNames.PRINTING_FINISHED, { driverRef });
            });
    }

    /**
     * @param {string} html
     * @param {PrintJobOptions} opts
     *
     * @return {Promise<any>}
     */
    printHtmlString(html, opts) {
        return new Promise((resolve, reject) => {
            if (!opts.hasOwnProperty('classes')) {
                opts.classes = [];
            }

            if (!opts.printRouting) {
                opts.printRouting = this.printRouter.getRoutingForType(opts.printType);
            }
            const driverRef = opts.printRouting.driverRef;

            // Add the print method as a CSS class.
            opts.classes.push('driver-' + driverRef);

            let footerPaddingEl = opts.addFooterDot !== false ? '<div id="footer-padding">.</div>' : '';

            // Because receipt templates are stored in the app repo it's annoying to change them.
            // They still only have <div id="receipt"> and are missing class="print-content"
            // This hack adds it in if it's missing.
            html = html.replace('<div id="receipt">', '<div id="receipt" class="print-content">');

            let documentHtml = `<html>
            <head>
            <!-- printCss -->
            </head>
            <body>
                <div id="printable" class="${opts.classes.join(' ')}">
                    ${html}
                    ${footerPaddingEl}
               </div>
            </body>
            </html>`;

            /* eslint-disable no-multi-assign */
            const builtDocument = document.implementation.createHTMLDocument('EPOS').documentElement.innerHTML = documentHtml;

            // When the printing is done resolve the promise.
            opts.onSuccess = () => resolve();
            opts.onFailure = (error) => reject(error);

            this.addToPrintQueue(builtDocument, opts);
        });
    }

    /**
     * @param {string} templateContents A string containing the HTML content of a template.
     * @param {object} data The data used by the template for rendering.
     * @param {PrintJobOptions} opts
     *
     * @return {Promise<any>}
     */
    printTemplate(templateContents, data, opts) {
        // See https://digitickets.atlassian.net/browse/PROP-396
        this.clickThePrintingWorkaroundButton();

        // Determine where to print this document, if it's not already specified.
        if (!opts.printRouting) {
            opts.printRouting = this.printRouter.getRoutingForType(opts.printType);
        }

        return this.templateCompiler
            .compile(
                templateContents,
                {
                    ...data,
                    headings: generateHeadings(data),
                    opts,
                }
            ).then((compiledHtml) => this.printHtmlString(compiledHtml, opts));
    }

    /**
     * @param {string} templatePath The path to the template relative to 'src' (typically 'print/templates/...').
     * @param {object} data The data used by the template for rendering.
     * @param opts
     * @param {PrintJobOptions} opts.
     * @return {Promise<any>}
     */
    printLocalTemplate(templatePath, data, opts) {
        // See https://digitickets.atlassian.net/browse/PROP-396
        this.clickThePrintingWorkaroundButton();

        const templateContent = this.getLocalTemplateContent(templatePath);
        return this.printTemplate(templateContent, data, opts);
    }

    /**
     * @param {string}templatePath
     *
     * @returns {string|null}
     */
    getLocalTemplateContent(templatePath) {
        return this.$templateCache.get(templatePath);
    }

    /**
     * See https://digitickets.atlassian.net/browse/PROP-396
     */
    clickThePrintingWorkaroundButton() {
        this.$timeout(() => {
            const btn = document.getElementById('printing-workaround-button');
            if (btn) {
                btn.click();
            }
        }, 100);
    }
}

module.exports = Printer;
